/**
 * Maintenance Schedule (Control Centre → Maintenance → Schedule)
 *
 * The forward-looking half of the maintenance page: what is legally or
 * operationally due, what has already lapsed, and which vehicles can have
 * several jobs done in one workshop visit.
 *
 * Ordered by what a fleet manager acts on first — an overdue MOT or LOLER
 * inspection is legal exposure, so it sits above the fold and everything else
 * follows.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertOctagon,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Info,
  RefreshCw,
  TriangleAlert,
  Truck,
} from 'lucide-react';
import ControlCentreKPICard from './ControlCentreKPICard';
import KPIDataModal, { ColumnDef } from './KPIDataModal';
import DataTable from '../DataTable';
import { API_ENDPOINTS } from '../../config/api';
import { authenticatedFetch } from '../../utils/apiClient';
import { MAINTENANCE_SCHEDULE_HISTORY_DAYS } from '../../utils/maintenanceScheduleWindows';
import { formatYmd, ukMonthGrid, ukTodayYmd } from '../../utils/ukTime';
import Skeleton from '../ui/Skeleton';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

// ── Contract with GET /fleet-intelligence/maintenance/schedule ──────────────

export type ObligationStatus = 'overdue' | 'due_soon' | 'scheduled' | 'completed';

export interface MaintenanceObligation {
  obligation_id: string;
  status: ObligationStatus;
  maintenance_type: string;
  due_date: string;
  completed_date?: string;
  days_until_due: number;
  on_time?: boolean;
  asset_kind: 'vehicle' | 'plant';
  vehicle_entity_id?: string;
  vehicle_registration?: string;
  /** Plant (forklifts, yard equipment) has an asset code instead of a plate. */
  asset_id?: string;
  vehicle_vin?: string;
  depot?: string;
  /** From Aquarius overdue-events — whether the asset was VOR at the time. */
  was_vor?: boolean;
  source_asserted_overdue: boolean;
  disagreement?: {
    derived_overdue: boolean;
    source_asserted_overdue: boolean;
    reason: string;
  };
  /**
   * Overdue on the last data we hold, but Aquarius has stopped reporting it as
   * upcoming or overdue and no completion closed it — treated as unconfirmed
   * and kept out of the overdue count. See `stale_reason`.
   */
  stale?: boolean;
  stale_reason?: string;
}

export interface VehicleObligationGroup {
  vehicle_entity_id?: string;
  vehicle_registration?: string;
  /** Plant (forklifts, yard equipment) has an asset code instead of a plate. */
  asset_id?: string;
  asset_kind: 'vehicle' | 'plant';
  depot?: string;
  overdue: number;
  due_soon: number;
  scheduled: number;
  next_due_date?: string;
  obligations: MaintenanceObligation[];
}

export interface MaintenanceScheduleData {
  obligations: MaintenanceObligation[];
  by_vehicle: VehicleObligationGroup[];
  summary: {
    overdue: number;
    due_within_7_days: number;
    due_within_30_days: number;
    completed_in_window: number;
    completed_on_time: number;
    on_time_completion_pct: number | null;
    assets_with_multiple_outstanding: number;
    disagreements: number;
    unconfirmed: number;
  };
}

interface MaintenanceScheduleViewProps {
  from: string;
  to: string;
  refreshKey?: number;
  /** Stamp parent visibility cooldown when a Schedule Iceberg fetch starts. */
  onTruthFetchStart?: () => void;
}

/** What the month calendar shows. Completions use completed_date; due work uses due_date. */
type CalendarMode = 'due' | 'completed' | 'both';

const CALENDAR_MODES: Array<{ id: CalendarMode; label: string; hint: string }> = [
  { id: 'due', label: 'Due work', hint: 'Outstanding jobs by due date' },
  {
    id: 'completed',
    label: 'Completed',
    hint: 'Finished jobs by completion date',
  },
  {
    id: 'both',
    label: 'Both',
    hint: 'Due work and recent completions together',
  },
];

// ── Formatting ──────────────────────────────────────────────────────────────

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function dayKey(iso: string): string {
  return iso.slice(0, 10);
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  return formatYmd(iso, { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Chronological sort key for due/completed dates (ms). */
function dueDateSortKey(iso: string | undefined): number {
  if (!iso) return 0;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? 0 : ms;
}

/** Plain English is what a manager scans for — "12 days overdue", not "-12". */
function formatDueLabel(days: number): string {
  if (days < -1) return `${Math.abs(days)} days overdue`;
  if (days === -1) return '1 day overdue';
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  return `Due in ${days} days`;
}

/** Matches the red / amber / green severity convention used across this page. */
function obligationToneClass(o: Pick<MaintenanceObligation, 'status' | 'on_time'>): string {
  if (o.status === 'completed') {
    // Late completion is still done, but not "all clear" green — amber flags it.
    return o.on_time === false ? 'text-warning' : 'text-success';
  }
  switch (o.status) {
    case 'overdue':
      return 'text-destructive';
    case 'due_soon':
      return 'text-warning';
    default:
      return 'text-foreground';
  }
}

function obligationChipClass(o: MaintenanceObligation): string {
  if (o.status === 'completed') {
    return o.on_time === false
      ? 'bg-warning/20 text-warning border-warning/40'
      : 'bg-success/15 text-success border-success/30';
  }
  switch (o.status) {
    case 'overdue':
      return 'bg-destructive/20 text-destructive border-destructive/40';
    case 'due_soon':
      return 'bg-warning/20 text-warning border-warning/40';
    default:
      return 'bg-ring/15 text-ring border-ring/30';
  }
}

function formatStatusLabel(o: MaintenanceObligation): string {
  if (o.status === 'completed') {
    const when = formatDate(o.completed_date);
    if (o.on_time === false) return `Completed late ${when}`;
    if (o.on_time === true) return `Completed on time ${when}`;
    return `Completed ${when}`;
  }
  return formatDueLabel(o.days_until_due);
}

function completionResultLabel(o: MaintenanceObligation): string {
  if (o.on_time === false) return 'Late';
  if (o.on_time === true) return 'On time';
  return '—';
}

function assetLabel(o: { vehicle_registration?: string; asset_id?: string; vehicle_vin?: string }): string {
  return o.vehicle_registration || o.asset_id || o.vehicle_vin || 'Unidentified asset';
}

const OBLIGATION_COLUMNS: ColumnDef[] = [
  { key: 'asset', header: 'Vehicle Reg' },
  { key: 'maintenance_type', header: 'Work due' },
  {
    key: 'due',
    header: 'Due date',
    sortValue: (row) => dueDateSortKey(String(row.due_date || '')),
  },
  {
    key: 'status_label',
    header: 'Status',
    render: (value, row) => (
      <span
        className={`font-medium ${obligationToneClass({
          status: (row.status as ObligationStatus | undefined) ?? 'scheduled',
          on_time: row.on_time as boolean | undefined,
        })}`}
      >
        {value == null || value === '' ? '—' : String(value)}
      </span>
    ),
  },
];

const OVERDUE_MODAL_COLUMNS: ColumnDef[] = [
  { key: 'asset', header: 'Vehicle Reg' },
  { key: 'maintenance_type', header: 'Work due' },
  {
    key: 'due',
    header: 'Due date',
    sortValue: (row) => dueDateSortKey(String(row.due_date || '')),
  },
  {
    key: 'overdue_by',
    header: 'Overdue by',
    sortValue: (row) => Number(row.overdue_by_days ?? 0),
    render: (value) => (
      <span className="font-medium text-destructive">
        {value == null || value === '' ? '—' : String(value)}
      </span>
    ),
  },
  {
    key: 'was_vor',
    header: 'Was VOR?',
    sortValue: (row) => {
      if (row.was_vor_flag === true) return 1;
      if (row.was_vor_flag === false) return 0;
      return -1;
    },
    render: (value) => (
      <span className={value === 'Yes' ? 'font-medium text-warning' : 'text-muted-foreground'}>
        {value == null || value === '' ? '—' : String(value)}
      </span>
    ),
  },
];

const COMPLETIONS_MODAL_COLUMNS: ColumnDef[] = [
  { key: 'asset', header: 'Vehicle Reg' },
  { key: 'maintenance_type', header: 'Work due' },
  {
    key: 'due',
    header: 'Due date',
    sortValue: (row) => dueDateSortKey(String(row.due_date || '')),
  },
  {
    key: 'completed',
    header: 'Completed',
    sortValue: (row) => dueDateSortKey(String(row.completed_date || '')),
  },
  {
    key: 'result',
    header: 'Result',
    render: (value) => (
      <span
        className={`font-medium ${
          value === 'Late'
            ? 'text-warning'
            : value === 'On time'
              ? 'text-success'
              : 'text-muted-foreground'
        }`}
      >
        {value == null || value === '' ? '—' : String(value)}
      </span>
    ),
  },
];

type ModalKind = 'overdue' | 'due' | 'completions' | 'disagreement' | 'unconfirmed';

/**
 * Group obligations by asset, mirroring the backend `groupByVehicle`.
 *
 * One implementation for every grouped view on this page — the flat "by
 * vehicle" toggle, a picked calendar day, and the work-type filter — so the
 * count and next-due rules cannot drift between them. Unconfirmed (stale)
 * obligations travel with their asset for context but never drive the counts
 * or the next-due date.
 */
function buildVehicleGroups(obligations: MaintenanceObligation[]): VehicleObligationGroup[] {
  const groups = new Map<string, VehicleObligationGroup>();
  for (const o of obligations) {
    const key = o.vehicle_entity_id || o.vehicle_registration || o.vehicle_vin || o.asset_id || 'unknown';
    let group = groups.get(key);
    if (!group) {
      group = {
        vehicle_entity_id: o.vehicle_entity_id,
        vehicle_registration: o.vehicle_registration,
        asset_id: o.asset_id,
        asset_kind: o.asset_kind,
        depot: o.depot,
        overdue: 0,
        due_soon: 0,
        scheduled: 0,
        obligations: [],
      };
      groups.set(key, group);
    }
    group.obligations.push(o);
    if (o.stale) continue;
    if (o.status === 'overdue') group.overdue++;
    else if (o.status === 'due_soon') group.due_soon++;
    else if (o.status === 'scheduled') group.scheduled++;
    if (o.status !== 'completed' && (!group.next_due_date || o.due_date < group.next_due_date)) {
      group.next_due_date = o.due_date;
    }
  }
  return [...groups.values()];
}

function toModalRow(o: MaintenanceObligation): Record<string, unknown> {
  return {
    asset: assetLabel(o),
    maintenance_type: o.maintenance_type,
    due: formatDate(o.due_date),
    due_date: o.due_date,
    completed: o.completed_date ? formatDate(o.completed_date) : '—',
    completed_date: o.completed_date,
    status_label: formatStatusLabel(o),
    status: o.status,
    on_time: o.on_time,
  };
}

function formatWasVor(wasVor: boolean | undefined): string {
  if (wasVor === true) return 'Yes';
  if (wasVor === false) return 'No';
  return '—';
}

function MaintenanceScheduleSkeleton() {
  return (
    <div className="space-y-6" role="status" aria-label="Loading maintenance schedule" aria-busy="true">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="h-28 rounded-lg border border-border bg-card p-4">
            <Skeleton className="h-4 w-28 mb-5" />
            <Skeleton className="h-8 w-16" />
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-5">
          <div>
            <Skeleton className="h-5 w-44 mb-2" />
            <Skeleton className="h-3 w-64" />
          </div>
          <Skeleton className="h-8 w-40" />
        </div>
        <div className="grid grid-cols-7 gap-2 mb-3">
          {Array.from({ length: 7 }, (_, index) => (
            <Skeleton key={index} className="h-3 w-8 mx-auto" />
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: 35 }, (_, index) => (
            <Skeleton key={index} className="h-19 w-full rounded-lg" />
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-5">
          <div>
            <Skeleton className="h-5 w-48 mb-2" />
            <Skeleton className="h-3 w-56" />
          </div>
          <Skeleton className="h-8 w-36" />
        </div>
        <div className="space-y-3">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-9 w-full" />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────────────────

export default function MaintenanceScheduleView({
  from,
  to,
  refreshKey = 0,
  onTruthFetchStart,
}: MaintenanceScheduleViewProps) {
  const [data, setData] = useState<MaintenanceScheduleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groupByVehicle, setGroupByVehicle] = useState(false);
  const [outstandingTableOpen, setOutstandingTableOpen] = useState(false);
  /** null = all work types. */
  const [workTypeFilter, setWorkTypeFilter] = useState<string | null>(null);
  const [calendarMode, setCalendarMode] = useState<CalendarMode>('both');
  /** YYYY-MM-DD when the outstanding/completed table is scoped to a calendar day. */
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null);
  const [monthOffset, setMonthOffset] = useState(0);
  const dayTableRef = useRef<HTMLDivElement>(null);
  const [modal, setModal] = useState<{
    open: boolean;
    kind: ModalKind;
    title: string;
    subtitle?: string;
    rows: Record<string, unknown>[];
  }>({ open: false, kind: 'due', title: '', rows: [] });

  // Identifies the most recent request. Changing the period can leave a slower
  // earlier fetch in flight; without this it resolves last and repaints the
  // page with the previous period's schedule.
  const requestSeq = useRef(0);

  const fetchSchedule = useCallback(async () => {
    const seq = ++requestSeq.current;
    onTruthFetchStart?.();
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.maintenanceSchedule({ from, to }));
      if (!res.ok) throw new Error(`Maintenance schedule failed (${res.status})`);
      const payload = (await res.json()) as MaintenanceScheduleData;
      if (seq !== requestSeq.current) return;
      setData(payload);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      console.error('[MaintenanceScheduleView] fetch failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to load maintenance schedule');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [from, to, onTruthFetchStart]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule, refreshKey]);

  // Memoised so the empty-state fallback keeps a stable reference — a fresh
  // [] each render would invalidate every filter below it.
  const obligations = useMemo(() => data?.obligations ?? [], [data]);

  const workTypes = useMemo(() => {
    const types = new Set<string>();
    for (const o of obligations) {
      if (o.maintenance_type) types.add(o.maintenance_type);
    }
    return [...types].sort((a, b) => a.localeCompare(b));
  }, [obligations]);

  const matchesWorkType = useCallback(
    (o: MaintenanceObligation) => !workTypeFilter || o.maintenance_type === workTypeFilter,
    [workTypeFilter],
  );

  // Unconfirmed obligations are excluded from overdue and outstanding — Aquarius
  // has stopped vouching for them, so they must not inflate the compliance
  // banner or the planning list. They are shown in an admin-only diagnostic.
  const overdue = useMemo(
    () => obligations.filter((o) => o.status === 'overdue' && !o.stale && matchesWorkType(o)),
    [obligations, matchesWorkType],
  );

  /** Outstanding work only — completed and unconfirmed jobs clutter the planning list. */
  const outstanding = useMemo(
    () => obligations.filter((o) => o.status !== 'completed' && !o.stale && matchesWorkType(o)),
    [obligations, matchesWorkType],
  );

  const completed = useMemo(
    () => obligations.filter((o) => o.status === 'completed' && matchesWorkType(o)),
    [obligations, matchesWorkType],
  );

  const disagreements = useMemo(() => obligations.filter((o) => o.disagreement), [obligations]);

  const overdueAssertionsFollowedByCompletion = useMemo(
    () =>
      disagreements.filter(
        (o) => o.completed_date && o.disagreement?.source_asserted_overdue && !o.disagreement.derived_overdue,
      ),
    [disagreements],
  );

  const otherDisagreements = useMemo(
    () => disagreements.filter((o) => !overdueAssertionsFollowedByCompletion.includes(o)),
    [disagreements, overdueAssertionsFollowedByCompletion],
  );

  const unconfirmed = useMemo(
    () => obligations.filter((o) => o.stale && matchesWorkType(o)),
    [obligations, matchesWorkType],
  );

  const filteredByVehicle = useMemo(() => {
    // Unfiltered, the server's grouping is authoritative. With a work-type
    // filter applied, regroup the matching outstanding obligations locally
    // through the shared helper so the counts follow the same rules.
    if (!workTypeFilter) return data?.by_vehicle ?? [];
    return buildVehicleGroups(
      obligations.filter((o) => o.status !== 'completed' && o.maintenance_type === workTypeFilter),
    );
  }, [data?.by_vehicle, obligations, workTypeFilter]);

  // ── Month calendar ────────────────────────────────────────────────────────

  const calendar = useMemo(() => {
    const { year, month, daysInMonth, leading, label } = ukMonthGrid(monthOffset);

    const byDay = new Map<string, MaintenanceObligation[]>();

    const place = (o: MaintenanceObligation, iso: string | undefined) => {
      if (!iso) return;
      const key = dayKey(iso);
      const list = byDay.get(key);
      if (list) list.push(o);
      else byDay.set(key, [o]);
    };

    // Due work lands on due_date; completions land on completed_date so a job
    // finished in March that was due in January does not appear on the wrong day.
    if (calendarMode === 'due' || calendarMode === 'both') {
      for (const o of outstanding) place(o, o.due_date);
    }
    if (calendarMode === 'completed' || calendarMode === 'both') {
      for (const o of completed) place(o, o.completed_date);
    }

    return {
      label,
      year,
      month,
      daysInMonth,
      leading,
      byDay,
      todayKey: ukTodayYmd(),
    };
  }, [outstanding, completed, monthOffset, calendarMode]);

  /** Table under the calendar: outstanding by default, or the day the user picked. */
  const tableObligations = useMemo(() => {
    if (!selectedDayKey) {
      if (calendarMode === 'completed') return completed;
      return outstanding;
    }
    const rows: MaintenanceObligation[] = [];
    if (calendarMode === 'due' || calendarMode === 'both') {
      for (const o of outstanding) {
        if (dayKey(o.due_date) === selectedDayKey) rows.push(o);
      }
    }
    if (calendarMode === 'completed' || calendarMode === 'both') {
      for (const o of completed) {
        if (o.completed_date && dayKey(o.completed_date) === selectedDayKey) rows.push(o);
      }
    }
    return rows;
  }, [selectedDayKey, calendarMode, outstanding, completed]);

  const tableByVehicle = useMemo(() => {
    if (!selectedDayKey && calendarMode !== 'completed') return filteredByVehicle;
    const jobs = calendarMode === 'due' ? tableObligations.filter((o) => o.status !== 'completed') : tableObligations;
    return buildVehicleGroups(jobs);
  }, [selectedDayKey, calendarMode, filteredByVehicle, tableObligations]);

  const selectDay = (key: string) => {
    setSelectedDayKey((prev) => (prev === key ? null : key));
    // Defer scroll until after the filtered table paints.
    requestAnimationFrame(() => {
      dayTableRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    });
  };

  // Drop the day filter when the user leaves that month or switches calendar mode
  // in a way that would empty the selection unexpectedly.
  useEffect(() => {
    setSelectedDayKey(null);
  }, [monthOffset, calendarMode]);

  // ── States ────────────────────────────────────────────────────────────────

  if (loading && !data) {
    return <MaintenanceScheduleSkeleton />;
  }

  if (error && !data) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <AlertOctagon className="w-12 h-12 text-destructive/60" />
        <p className="text-destructive text-sm">{error}</p>
        <Button
          variant="secondary"
          size="sm"
          type="button"
          onClick={fetchSchedule}
          className="rounded-lg"
        >
          <RefreshCw className="w-4 h-4" />
          Try again
        </Button>
      </div>
    );
  }

  if (!data) return null;

  // KPI values track the active work-type filter so card counts match the modal.
  const dueWithin7 = outstanding.filter((o) => o.days_until_due >= 0 && o.days_until_due <= 7);
  const dueWithin30 = outstanding.filter((o) => o.days_until_due >= 0 && o.days_until_due <= 30);
  const completedOnTimeCount = completed.filter((o) => o.on_time === true).length;
  const onTimeCompletionPct =
    completed.length === 0 ? null : Math.round((completedOnTimeCount / completed.length) * 100);

  const modalColumns =
    modal.kind === 'disagreement'
      ? [...OBLIGATION_COLUMNS, { key: 'reason', header: 'Why they differ' }]
      : modal.kind === 'unconfirmed'
        ? [...OBLIGATION_COLUMNS, { key: 'reason', header: 'Why unconfirmed' }]
        : modal.kind === 'overdue'
          ? OVERDUE_MODAL_COLUMNS
          : modal.kind === 'completions'
            ? COMPLETIONS_MODAL_COLUMNS
            : OBLIGATION_COLUMNS;

  return (
    <div className="space-y-6">
      {/* 1 — KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
        <ControlCentreKPICard
          className="h-full"
          title="Overdue now"
          value={overdue.length}
          subtitle={overdue.length > 0 ? 'Action required' : 'All clear'}
          icon={AlertOctagon}
          colorScheme={overdue.length > 0 ? 'red' : 'green'}
          onClick={
            overdue.length > 0
              ? () =>
                  setModal({
                    open: true,
                    kind: 'overdue',
                    title: 'Overdue maintenance',
                    subtitle: 'Past its due date with no completion recorded',
                    rows: overdue.map((o) => ({
                      ...toModalRow(o),
                      due_date: o.due_date,
                      overdue_by: `${Math.abs(o.days_until_due)} days`,
                      overdue_by_days: Math.abs(o.days_until_due),
                      was_vor: formatWasVor(o.was_vor),
                      was_vor_flag: o.was_vor,
                    })),
                  })
              : undefined
          }
        />
        <ControlCentreKPICard
          className="h-full"
          title="Due in 7 days"
          value={dueWithin7.length}
          subtitle="Book workshop time now"
          icon={CalendarClock}
          colorScheme="amber"
          onClick={() =>
            setModal({
              open: true,
              kind: 'due',
              title: 'Due within 7 days',
              rows: dueWithin7.map(toModalRow),
            })
          }
        />
        <ControlCentreKPICard
          className="h-full"
          title="Due in 30 days"
          value={dueWithin30.length}
          subtitle="Plan next month's workshop load"
          icon={CalendarDays}
          colorScheme="blue"
          onClick={() =>
            setModal({
              open: true,
              kind: 'due',
              title: 'Due within 30 days',
              rows: dueWithin30.map(toModalRow),
            })
          }
        />
        <ControlCentreKPICard
          className="h-full"
          title="Completed on time"
          value={onTimeCompletionPct === null ? '—' : `${onTimeCompletionPct}%`}
          subtitle={
            onTimeCompletionPct === null
              ? `No completions in the last ${MAINTENANCE_SCHEDULE_HISTORY_DAYS} days`
              : `${completedOnTimeCount} of ${completed.length} in last ${MAINTENANCE_SCHEDULE_HISTORY_DAYS} days`
          }
          icon={CheckCircle2}
          colorScheme={onTimeCompletionPct === null ? 'green' : onTimeCompletionPct < 80 ? 'amber' : 'green'}
          onClick={
            completed.length > 0
              ? () =>
                  setModal({
                    open: true,
                    kind: 'completions',
                    title: `Completions in the last ${MAINTENANCE_SCHEDULE_HISTORY_DAYS} days`,
                    subtitle:
                      onTimeCompletionPct === null
                        ? undefined
                        : `${completedOnTimeCount} of ${completed.length} on time (${onTimeCompletionPct}%)`,
                    rows: [...completed]
                      .sort((a, b) => {
                        // Late jobs first — that is what the manager needs to review.
                        const aRank = a.on_time === false ? 0 : 1;
                        const bRank = b.on_time === false ? 0 : 1;
                        if (aRank !== bRank) return aRank - bRank;
                        return (b.completed_date || '').localeCompare(a.completed_date || '');
                      })
                      .map((o) => ({
                        asset: assetLabel(o),
                        maintenance_type: o.maintenance_type,
                        due: formatDate(o.due_date),
                        due_date: o.due_date,
                        completed: formatDate(o.completed_date),
                        completed_date: o.completed_date,
                        result: completionResultLabel(o),
                      })),
                  })
              : undefined
          }
        />
      </div>

      {overdueAssertionsFollowedByCompletion.length > 0 && (
        <div className="rounded-lg border border-border bg-card/60 p-3 flex items-start gap-3">
          <Info className="w-4 h-4 text-ring shrink-0 mt-0.5" />
          <div className="text-xs text-foreground">
            <span className="font-medium text-ring">Overdue assertion followed by completion</span> Aquarius
            previously listed {overdueAssertionsFollowedByCompletion.length === 1 ? 'this job' : 'these jobs'} as
            overdue, and a completion has since been recorded.
            <Button
              variant="link"
              size="sm"
              type="button"
              onClick={() =>
                setModal({
                  open: true,
                  kind: 'disagreement',
                  title: 'Overdue assertion followed by completion',
                  subtitle: 'Aquarius listed these jobs as overdue before their completion was recorded',
                  rows: overdueAssertionsFollowedByCompletion.map((o) => ({
                    ...toModalRow(o),
                    reason: o.disagreement?.reason ?? 'Not available',
                  })),
                })
              }
              className="ml-1 text-ring"
            >
              Review
            </Button>
          </div>
        </div>
      )}

      {otherDisagreements.length > 0 && (
        <div className="rounded-lg border border-border bg-card/60 p-3 flex items-start gap-3">
          <Info className="w-4 h-4 text-ring shrink-0 mt-0.5" />
          <div className="text-xs text-foreground">
            <span className="font-medium text-ring">
              {otherDisagreements.length} {otherDisagreements.length === 1 ? 'job disagrees' : 'jobs disagree'} with
              Aquarius.
            </span>{' '}
            Status above is worked out from the due date against today. Aquarius' own overdue list is a snapshot from
            its last sync, so the two can differ.
            <Button
              variant="link"
              size="sm"
              type="button"
              onClick={() =>
                setModal({
                  open: true,
                  kind: 'disagreement',
                  title: 'Where our status differs from Aquarius',
                  subtitle: 'Both readings are shown rather than one being picked silently',
                  rows: otherDisagreements.map((o) => ({
                    ...toModalRow(o),
                    reason: o.disagreement?.reason ?? 'Not available',
                  })),
                })
              }
              className="ml-1 text-ring"
            >
              Review
            </Button>
          </div>
        </div>
      )}

      {unconfirmed.length > 0 && (
        <div className="rounded-lg border border-border bg-card/60 p-3 flex items-start gap-3">
          <Info className="w-4 h-4 text-foreground shrink-0 mt-0.5" />
          <div className="text-xs text-foreground">
            <span className="font-medium text-foreground">
              {unconfirmed.length} {unconfirmed.length === 1 ? 'obligation is' : 'obligations are'} unconfirmed.
            </span>{' '}
            Past due on the last data we hold, but Aquarius has since stopped listing them as upcoming or overdue, most
            likely completed or rescheduled outside the loaded window. They are held out of the overdue count until the
            source confirms them either way.
            <Button
              variant="link"
              size="sm"
              type="button"
              onClick={() =>
                setModal({
                  open: true,
                  kind: 'unconfirmed',
                  title: 'Unconfirmed obligations',
                  subtitle: 'Overdue on our data; Aquarius has stopped reporting them',
                  rows: unconfirmed.map((o) => ({
                    ...toModalRow(o),
                    reason: o.stale_reason ?? 'Not available',
                  })),
                })
              }
              className="ml-1 text-foreground hover:text-foreground"
            >
              Review
            </Button>
          </div>
        </div>
      )}

      {/* 3 — Month calendar */}
      <div className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <div>
            <h4 className="text-base font-semibold text-foreground">Maintenance calendar</h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              {calendarMode === 'due'
                ? 'Outstanding jobs by due date.'
                : calendarMode === 'completed'
                  ? `Finished jobs by completion date (last ${MAINTENANCE_SCHEDULE_HISTORY_DAYS} days).`
                  : 'Due work by due date, completions by completion date.'}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <div
              className="inline-flex rounded-lg border border-border bg-background/50 p-0.5"
              role="group"
              aria-label="Calendar contents"
            >
              {CALENDAR_MODES.map((mode) => (
                <Button
                  key={mode.id}
                  variant="ghost"
                  size="sm"
                  type="button"
                  title={mode.hint}
                  onClick={() => setCalendarMode(mode.id)}
                  className={cn(
                    'rounded-md font-medium',
                    calendarMode === mode.id
                      ? 'bg-warning/20 text-warning'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {mode.label}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="icon"
                type="button"
                onClick={() => setMonthOffset((m) => m - 1)}
                className="rounded-lg border border-border bg-secondary/50"
                aria-label="Previous month"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-semibold text-foreground min-w-35 text-center">
                {calendar.label}
              </span>
              <Button
                variant="secondary"
                size="icon"
                type="button"
                onClick={() => setMonthOffset((m) => m + 1)}
                className="rounded-lg border border-border bg-secondary/50"
                aria-label="Next month"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-2 text-xs">
          {WEEKDAYS.map((d) => (
            <div key={d} className="text-center text-muted-foreground font-medium py-1">
              {d}
            </div>
          ))}
          {Array.from({ length: calendar.leading }).map((_, i) => (
            <div key={`pad-${i}`} className="min-h-20" />
          ))}
          {Array.from({ length: calendar.daysInMonth }).map((_, i) => {
            const day = i + 1;
            const key = `${calendar.year}-${String(calendar.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const jobs = calendar.byDay.get(key) ?? [];
            const isToday = key === calendar.todayKey;
            const hasOverdue = jobs.some((j) => j.status === 'overdue');
            const hasDueSoon = jobs.some((j) => j.status === 'due_soon');
            const hasCompleted = jobs.some((j) => j.status === 'completed');
            const onlyCompleted = hasCompleted && jobs.every((j) => j.status === 'completed');
            const hasLateCompletion = jobs.some((j) => j.status === 'completed' && j.on_time === false);

            // Hover keeps the same tint (brightness-only, as before); the explicit
            // hover:bg-* overrides the Button variant's hover background.
            let tone = 'bg-secondary/30 border-border hover:bg-secondary/30';
            if (hasOverdue) tone = 'bg-destructive/20 border-destructive/50 hover:bg-destructive/20';
            else if (hasDueSoon) tone = 'bg-warning/15 border-warning/40 hover:bg-warning/15';
            else if (onlyCompleted && hasLateCompletion) tone = 'bg-warning/15 border-warning/40 hover:bg-warning/15';
            else if (onlyCompleted) tone = 'bg-success/15 border-success/40 hover:bg-success/15';
            else if (jobs.length > 0) tone = 'bg-ring/15 border-ring/30 hover:bg-ring/15';

            const isSelected = selectedDayKey === key;

            return (
              <Button
                key={key}
                type="button"
                variant="secondary"
                size="sm"
                disabled={jobs.length === 0}
                onClick={() => selectDay(key)}
                aria-pressed={isSelected}
                className={cn(
                  'min-h-20 flex-col items-stretch justify-start rounded-lg border p-1.5 text-left font-normal',
                  tone,
                  jobs.length > 0 ? 'hover:brightness-125 cursor-pointer' : 'cursor-default',
                  isSelected ? 'ring-2 ring-warning/80' : isToday ? 'ring-2 ring-ring/70' : '',
                  // Empty cells keep full opacity (the raw button never dimmed them).
                  'disabled:opacity-100',
                )}
              >
                <div className="flex items-center justify-between">
                  <span className={`font-semibold ${isToday ? 'text-ring' : 'text-foreground'}`}>
                    {day}
                  </span>
                  {jobs.length > 2 && (
                    <span className="text-xs text-muted-foreground">{jobs.length}</span>
                  )}
                </div>
                <div className="mt-1 space-y-0.5">
                  {jobs.slice(0, 2).map((j) => (
                    <div
                      key={`${j.obligation_id}-${j.status}`}
                      className={`truncate text-xs leading-tight ${obligationToneClass(j)}`}
                      title={`${assetLabel(j)} — ${j.maintenance_type}${
                        j.status === 'completed' ? ` · ${formatStatusLabel(j)}` : ''
                      }`}
                    >
                      {j.status === 'completed' ? (
                        j.on_time === false ? (
                          <TriangleAlert className="inline w-3 h-3 text-warning mr-0.5 -mt-0.5" aria-hidden />
                        ) : (
                          '✓ '
                        )
                      ) : null}
                      {assetLabel(j)}
                    </div>
                  ))}
                  {jobs.length > 2 && (
                    <div className="text-xs text-muted-foreground">+{jobs.length - 2} more</div>
                  )}
                </div>
              </Button>
            );
          })}
        </div>
      </div>

      {/* 4 — Flat list or grouped by vehicle (optionally scoped to a calendar day) */}
      <div ref={dayTableRef} className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <div>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              aria-expanded={outstandingTableOpen}
              aria-controls="maintenance-schedule-work-table"
              onClick={() => setOutstandingTableOpen((open) => !open)}
              className="h-auto rounded-none px-0 text-left text-base font-semibold text-foreground hover:bg-transparent"
            >
              {selectedDayKey
                ? `Work on ${formatDate(selectedDayKey)}`
                : calendarMode === 'completed'
                  ? groupByVehicle
                    ? 'Completed work by vehicle'
                    : 'Completed work'
                  : groupByVehicle
                    ? 'Outstanding work by vehicle'
                    : 'All outstanding work'}
              <ChevronDown
                className={`w-4 h-4 transition-transform ${outstandingTableOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </Button>
            {(selectedDayKey || groupByVehicle || calendarMode === 'completed') && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {selectedDayKey
                  ? `${tableObligations.length} job${tableObligations.length === 1 ? '' : 's'} on this day${
                      workTypeFilter ? ` · ${workTypeFilter}` : ''
                    }. Click the date again to clear.`
                  : groupByVehicle
                    ? 'Vehicles with several jobs due can be taken off the road once instead of three times.'
                    : `${completed.length} completed in the last ${MAINTENANCE_SCHEDULE_HISTORY_DAYS} days${
                        workTypeFilter ? ` · ${workTypeFilter}` : ''
                      }.`}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {selectedDayKey && (
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => setSelectedDayKey(null)}
                className="h-auto rounded-lg border border-warning/50 bg-warning/10 px-2.5 py-1 text-warning hover:bg-warning/20 hover:text-warning"
              >
                Clear date
              </Button>
            )}
            <div className="flex items-center gap-1.5 flex-wrap" role="group" aria-label="Filter by work due type">
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => setWorkTypeFilter(null)}
                className={cn(
                  'h-auto rounded-lg border px-2.5 py-1',
                  workTypeFilter === null
                    ? 'border-warning/50 bg-warning/20 text-warning hover:bg-warning/20'
                    : 'border-border bg-secondary/50 text-foreground',
                )}
              >
                All
              </Button>
              {workTypes.map((type) => (
                <Button
                  key={type}
                  variant="secondary"
                  size="sm"
                  type="button"
                  onClick={() => setWorkTypeFilter(type)}
                  title={type}
                  className={cn(
                    'h-auto max-w-40 truncate rounded-lg border px-2.5 py-1',
                    workTypeFilter === type
                      ? 'border-warning/50 bg-warning/20 text-warning hover:bg-warning/20'
                      : 'border-border bg-secondary/50 text-foreground',
                  )}
                >
                  {type}
                </Button>
              ))}
            </div>
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={() => setGroupByVehicle((g) => !g)}
              className={cn(
                'h-auto rounded-lg border px-3 py-1.5',
                groupByVehicle
                  ? 'border-ring/50 bg-ring/20 text-ring hover:bg-ring/20'
                  : 'border-border bg-secondary/50 text-foreground',
              )}
            >
              <Truck className="w-3.5 h-3.5" />
              Group by vehicle
            </Button>
          </div>
        </div>

        {outstandingTableOpen && (
          <div id="maintenance-schedule-work-table">
            {groupByVehicle ? (
              <div className="space-y-2">
                {tableByVehicle
                  .filter((g) =>
                    selectedDayKey || calendarMode === 'completed'
                      ? g.obligations.length > 0
                      : g.overdue + g.due_soon + g.scheduled > 0,
                  )
                  .map((g) => {
                    const jobs =
                      selectedDayKey || calendarMode === 'completed'
                        ? g.obligations
                        : g.obligations.filter((o) => o.status !== 'completed');
                    return (
                      <div
                        key={g.vehicle_entity_id || g.vehicle_registration || g.asset_id || 'unknown'}
                        className="rounded-lg border border-border bg-background/40 p-3"
                      >
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-2 min-w-0">
                            <Truck className="w-4 h-4 text-muted-foreground shrink-0" />
                            <span className="font-medium text-white truncate">
                              {g.vehicle_registration || g.asset_id || g.vehicle_entity_id || 'Unidentified asset'}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {jobs.every((o) => o.status === 'completed')
                              ? `${jobs.length} completed`
                              : `Next due ${formatDate(g.next_due_date)}`}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {jobs.map((o) => (
                            <span
                              key={`${o.obligation_id}-${o.status}`}
                              className={`text-xs px-2 py-0.5 rounded border ${obligationChipClass(o)}`}
                              title={
                                o.status === 'completed'
                                  ? `${o.maintenance_type} — ${formatStatusLabel(o)} (due ${formatDate(o.due_date)})`
                                  : `${o.maintenance_type} — ${formatDate(o.due_date)}`
                              }
                            >
                              {o.status === 'completed'
                                ? `${o.maintenance_type} · ${completionResultLabel(o).toLowerCase()} ${formatDate(o.completed_date)}`
                                : `${o.maintenance_type} · ${formatDueLabel(o.days_until_due)}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                {tableByVehicle.length === 0 && (
                  <p className="text-sm text-muted-foreground py-6 text-center">
                    {selectedDayKey ? 'No work on this day' : 'No outstanding maintenance'}
                  </p>
                )}
              </div>
            ) : (
              <DataTable
                hideExport
                maxRows={12}
                hoverColor="blue"
                disableRowHover
                emptyMessage={selectedDayKey ? 'No work on this day' : 'No outstanding maintenance'}
                columns={[
                  { key: 'asset', label: 'Vehicle Reg' },
                  { key: 'maintenance_type', label: 'Work due', wrap: true },
                  { key: 'due', label: 'Due date' },
                  { key: 'status_label', label: 'Status' },
                ]}
                data={tableObligations.map((o) => ({
                  id: `${o.obligation_id}-${o.status}`,
                  asset: (
                    <span className="font-medium text-white">
                      {assetLabel(o)}
                      {o.asset_kind === 'plant' && (
                        <span
                          className="ml-2 text-xs uppercase tracking-wide text-muted-foreground"
                          title="Non-vehicle equipment (forklift, yard plant, etc.)"
                        >
                          Plant
                        </span>
                      )}
                    </span>
                  ),
                  maintenance_type: o.maintenance_type,
                  due: formatDate(o.due_date),
                  due_sortKey: dueDateSortKey(o.due_date),
                  status_label: <span className={`font-medium ${obligationToneClass(o)}`}>{formatStatusLabel(o)}</span>,
                }))}
              />
            )}
          </div>
        )}
      </div>

      <KPIDataModal
        isOpen={modal.open}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        title={modal.title}
        subtitle={modal.subtitle}
        icon={CalendarDays}
        columns={modalColumns}
        data={modal.rows}
        searchKeys={['asset', 'maintenance_type', 'reason', 'was_vor', 'result', 'completed']}
        footerNote="Aquarius upcoming, overdue and completed event feeds. On-time means completed on or before the due calendar day."
      />
    </div>
  );
}
